import Sidebar from "../components/Sidebar";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { ROUTES } from "../App";
import MobileHeader from "../components/MobileHeader";
import { useEffect, useMemo, useState } from "react";
import { database } from "../constants/firebase";
import { ref, onValue } from "firebase/database";
import {
  BsChevronRight,
  BsBriefcase,
  BsBoxSeam,
  BsShop,
  BsLock,
  BsLightningCharge,
  BsPeopleFill,
} from "react-icons/bs";

// ─── helpers ────────────────────────────────────────────────────────────────

const timeAgo = (ts) => {
  if (!ts) return "";
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const ACTIVE_THRESHOLD = 15 * 60 * 1000; // 15 min

// ─── sub-components ─────────────────────────────────────────────────────────

const SectionRow = ({ icon: Icon, iconBg, label, sub, onClick }) => (
  <button
    onClick={onClick}
    className="group w-full flex items-center justify-between px-4 py-4 min-h-[76px] rounded-2xl bg-gray-50 dark:bg-[#242424] border border-gray-200 dark:border-white/10 shadow-[0_1px_0_rgba(0,0,0,0.04)] dark:shadow-none hover:border-[#327691]/60 hover:bg-white dark:hover:bg-[#2B2B2B] active:scale-[0.98] transition-all duration-150"
  >
    <div className="flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${iconBg}`}>
        <Icon className="text-white text-lg" />
      </div>
      <div className="text-left">
        <p className="font-semibold text-[15px] leading-tight text-black dark:text-white">{label}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</p>
      </div>
    </div>
    <BsChevronRight className="text-gray-400 dark:text-gray-500 group-hover:text-[#327691] text-base flex-shrink-0 transition-colors" />
  </button>
);

const ComingSoonTab = ({ title, description }) => (
  <div className="flex flex-col items-center justify-center py-20 gap-4 text-center px-6">
    <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-white/10 flex items-center justify-center">
      <BsLock className="text-2xl text-gray-400 dark:text-gray-500" />
    </div>
    <p className="text-lg font-bold text-black dark:text-white">{title}</p>
    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">{description}</p>
    <span className="mt-1 px-4 py-1.5 rounded-full text-xs font-semibold bg-[#327691]/10 text-[#327691] border border-[#327691]/30">
      Coming Soon
    </span>
  </div>
);

// ─── main component ──────────────────────────────────────────────────────────

const TABS = ["My Studio", "What's Happening", "Coming Soon", "Coming Soon"];

const Studio = () => {
  const navigate = useMoveTo();
  const { currentUser, isHost } = useUser();
  const [activeTab, setActiveTab] = useState(0);

  // counts for My Studio sections
  const [productCount, setProductCount] = useState(null);
  const [listingCount, setListingCount] = useState(null);
  const [jobCount, setJobCount] = useState(null);

  // What's Happening data
  const [latestJobs, setLatestJobs] = useState([]);
  const [allUsers, setAllUsers] = useState({});

  // ── fetch product/listing counts ────────────────────────────────────────
  useEffect(() => {
    if (!currentUser?.uid) {
      setProductCount(0);
      setListingCount(0);
      return;
    }

    const listingsRef = ref(database, "Listings");
    const unsubscribe = onValue(listingsRef, (snap) => {
      if (!snap.exists()) {
        setProductCount(0);
        setListingCount(0);
        return;
      }
      const allListings = Object.values(snap.val());
      const mine = allListings.filter((item) => item.userId === currentUser.uid);

      // In this codebase, digital products/courses are also stored in `Listings`.
      const products = mine.filter((item) => normalizeText(item.type) === "course").length;
      const listings = mine.filter((item) => normalizeText(item.type) !== "course").length;

      setProductCount(products);
      setListingCount(listings);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  // ── fetch job counts ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser?.uid) {
      setJobCount(0);
      return;
    }

    const jobsRef = ref(database, "Jobs");
    const unsubscribe = onValue(jobsRef, (snap) => {
      if (!snap.exists()) {
        setJobCount(0);
        return;
      }
      const data = snap.val();
      const myJobs = Object.values(data).filter(
        (j) => j.hostId === currentUser.uid && j.status !== "closed"
      );
      setJobCount(myJobs.length);
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  // ── fetch What's Happening data ─────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 1) return;

    const jobsRef = ref(database, "Jobs");
    const usersRef = ref(database, "Users");

    const unsubscribeJobs = onValue(jobsRef, (snap) => {
      if (!snap.exists()) {
        setLatestJobs([]);
        return;
      }

      const userNiche = normalizeText(currentUser?.niche || currentUser?.role || "");
      const all = Object.entries(snap.val())
        .map(([id, j]) => ({ id, ...j }))
        .filter((j) => j.status !== "closed" && j.hostId !== currentUser?.uid)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      const nicheFirst = userNiche
        ? all.filter((job) => {
            const haystack = [job.title, job.role, job.type, job.companyName]
              .map(normalizeText)
              .join(" ");
            return haystack.includes(userNiche);
          })
        : [];

      const selected = (nicheFirst.length ? nicheFirst : all).slice(0, 8);
      setLatestJobs(selected);
    });

    const unsubscribeUsers = onValue(usersRef, (snap) => {
      if (snap.exists()) setAllUsers(snap.val());
    });

    return () => {
      unsubscribeJobs();
      unsubscribeUsers();
    };
  }, [activeTab, currentUser?.uid, currentUser?.niche, currentUser?.role]);

  // ── online friends ───────────────────────────────────────────────────────
  const onlineFriends = useMemo(() => {
    if (!currentUser?.uid || !Object.keys(allUsers).length) return [];
    const friendIds = Object.keys(currentUser.friends || {});
    return friendIds
      .map((fid) => allUsers[fid])
      .filter((u) => u && u.lastSeen && Date.now() - u.lastSeen < ACTIVE_THRESHOLD)
      .slice(0, 10);
  }, [allUsers, currentUser]);

  // ── stat label helper ────────────────────────────────────────────────────
  const statLabel = (count, singular, plural) => {
    if (count === null) return "Loading…";
    return `${count} ${count === 1 ? singular : plural}`;
  };

  return (
    <div className="flex flex-col h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-hidden">
      {/* Mobile Header */}
      <MobileHeader
        title="Studio"
        rightElement={
          isHost ? (
            <button
              onClick={() => navigate(ROUTES.SUBSCRIPTION_PLAN)}
              className="px-4 py-2 rounded-md font-semibold text-xs bg-[#fef8dc] dark:bg-[#f6e38c11] text-[#EFBF04] dark:text-[#EFBF04] border border-[#EFBF04]"
            >
              👑 Pro Member
            </button>
          ) : (
            <button
              onClick={() => navigate(ROUTES.SUBSCRIPTION_PLAN)}
              className="px-4 py-2 rounded-md font-semibold text-xs border border-[#327691] bg-[#327691] text-white"
            >
              Access Pro
            </button>
          )
        }
      />

      <div className="flex flex-1 overflow-hidden">
        <Sidebar />

        <main className="flex-1 overflow-y-auto pb-24 md:pb-5 bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
          {/* Desktop page title */}
          <div className="hidden md:flex items-center justify-between px-6 pt-5 pb-4">
            <h2 className="text-2xl font-bold">Studio</h2>
            {isHost ? (
              <button
                onClick={() => navigate(ROUTES.SUBSCRIPTION_PLAN)}
                className="px-4 py-2 rounded-xl font-semibold text-sm bg-[#fef8dc] dark:bg-[#f6e38c11] text-[#EFBF04] dark:text-[#EFBF04] border border-[#EFBF04]"
              >
                👑 Pro Member
              </button>
            ) : (
              <button
                onClick={() => navigate(ROUTES.SUBSCRIPTION_PLAN)}
                className="px-4 py-2 rounded-xl font-semibold text-sm border border-[#327691] bg-[#327691] text-white"
              >
                Access Pro
              </button>
            )}
          </div>

          {/* Tab bar */}
          <div className="sticky top-0 z-10 flex gap-2 overflow-x-auto px-4 md:px-6 py-3 md:py-2 scrollbar-hide border-b border-gray-200 dark:border-white/10 bg-white/90 dark:bg-[#1A1A1A]/90 backdrop-blur-sm">
            {TABS.map((tab, i) => (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className={`flex-shrink-0 min-h-[40px] px-4 py-2 rounded-xl text-xs md:text-sm font-semibold whitespace-nowrap transition border border-[#327691] ${
                  activeTab === i
                    ? "bg-[#327691] text-white"
                    : "bg-transparent text-primary dark:text-white"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* ─── Tab 0: My Studio ──────────────────────────────────────── */}
          {activeTab === 0 && (
            <div className="px-4 md:px-6 py-5 md:py-6 flex flex-col gap-4">
              <SectionRow
                icon={BsBoxSeam}
                iconBg="bg-[#327691]"
                label="My Digital Products"
                sub={statLabel(productCount, "product listed", "products listed")}
                onClick={() => navigate(`${ROUTES.MY_PRODUCTS}?tab=products&view=digital`)}
              />
              <SectionRow
                icon={BsShop}
                iconBg="bg-amber-500"
                label="My Listings"
                sub={statLabel(listingCount, "listing", "listings")}
                onClick={() => navigate(`${ROUTES.MY_PRODUCTS}?tab=products&view=listings`)}
              />
              <SectionRow
                icon={BsBriefcase}
                iconBg="bg-purple-600"
                label="My Job Posts"
                sub={statLabel(jobCount, "active job", "active jobs")}
                onClick={() => navigate(ROUTES.JOBS_MY_POSTS)}
              />

              {/* Quick actions */}
              <div className="mt-1">
                <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.08em] mb-3 px-1">
                  Quick Actions
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: "Add Product", route: ROUTES.ADD_PRODUCT, color: "bg-[#327691]" },
                    { label: "Post a Job", route: ROUTES.JOBS_POST, color: "bg-purple-600" },
                    { label: "My Community", route: ROUTES.MY_COMMUNITY, color: "bg-teal-600" },
                    { label: "Subscription", route: ROUTES.SUBSCRIPTION_PLAN, color: "bg-amber-500" },
                  ].map((action, i) => (
                    <button
                      key={i}
                      onClick={() => navigate(action.route)}
                      className={`${action.color} text-white text-sm font-semibold rounded-2xl px-4 py-3.5 min-h-[52px] text-left flex items-center justify-between active:scale-95 transition-transform duration-150`}
                    >
                      <span>{action.label}</span>
                      <BsChevronRight className="text-white/70 text-xs" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── Tab 1: What's Happening ───────────────────────────────── */}
          {activeTab === 1 && (
            <div className="px-4 md:px-6 py-5 md:py-6 flex flex-col gap-5">

              {/* Online Friends */}
              <section className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50/70 dark:bg-[#242424]/80 p-4">
                <div className="flex items-center gap-2 mb-3.5">
                  <BsPeopleFill className="text-[#327691] text-base" />
                  <p className="font-bold text-sm tracking-tight text-black dark:text-white">
                    Friends Online
                  </p>
                  {onlineFriends.length > 0 && (
                    <span className="ml-auto text-xs text-[#327691] font-semibold bg-[#327691]/10 border border-[#327691]/20 rounded-full px-2 py-0.5">
                      {onlineFriends.length} active
                    </span>
                  )}
                </div>
                {onlineFriends.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 px-1 py-1">
                    None of your friends are online right now.
                  </p>
                ) : (
                  <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide">
                    {onlineFriends.map((u, i) => (
                      <div key={i} className="flex flex-col items-center gap-1.5 flex-shrink-0">
                        <div className="relative">
                          {u.profilePicture ? (
                            <img
                              src={u.profilePicture}
                              alt={u.username || "User"}
                              className="w-14 h-14 rounded-full object-cover border-2 border-[#327691]"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-full bg-[#327691] flex items-center justify-center text-white font-bold text-sm border-2 border-[#327691]">
                              {(u.username || "?")[0].toUpperCase()}
                            </div>
                          )}
                          <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 border-2 border-white dark:border-[#1A1A1A]" />
                        </div>
                        <p className="text-[11px] text-gray-600 dark:text-gray-400 max-w-[62px] truncate text-center">
                          {u.username || "User"}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Latest Jobs */}
              <section className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50/70 dark:bg-[#242424]/80 p-4">
                <div className="flex items-center gap-2 mb-3.5">
                  <BsLightningCharge className="text-amber-500 text-base" />
                  <p className="font-bold text-sm tracking-tight text-black dark:text-white">
                    Niche Job Alerts
                  </p>
                </div>
                {latestJobs.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 px-1 py-1">
                    No matching job alerts right now.
                  </p>
                ) : (
                  <div className="flex flex-col gap-2.5">
                    {latestJobs.map((job) => {
                      const poster = allUsers[job.hostId];
                      const posterName =
                        poster?.username || poster?.name || job.hostName || "Unknown";
                      return (
                        <button
                          key={job.id}
                          onClick={() => navigate(ROUTES.JOBS_FIND)}
                          className="w-full flex items-start gap-3 p-3.5 rounded-2xl bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-white/10 hover:border-[#327691]/50 text-left active:scale-[0.98] transition-all duration-150"
                        >
                          {poster?.profilePicture || poster?.profilePic ? (
                            <img
                              src={poster?.profilePicture || poster?.profilePic}
                              alt=""
                              className="w-11 h-11 rounded-xl object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-xl bg-[#327691]/20 flex items-center justify-center flex-shrink-0">
                              <BsBriefcase className="text-[#327691] text-base" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-[15px] leading-tight font-semibold text-black dark:text-white truncate">
                              {job.title || "Untitled Job"}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">
                              {posterName} · {timeAgo(job.createdAt)}
                            </p>
                            <div className="mt-2 flex items-center gap-2">
                              {job.role && (
                                <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-white/10">
                                  {job.role}
                                </span>
                              )}
                              {job.budget && (
                                <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#327691]/10 text-[#327691] border border-[#327691]/20">
                                  {job.budget}
                                </span>
                              )}
                            </div>
                          </div>
                          <BsChevronRight className="text-gray-400 dark:text-gray-500 text-sm flex-shrink-0 mt-1" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          )}

          {/* ─── Tab 2: Coming Soon ────────────────────────────────────── */}
          {activeTab === 2 && (
            <ComingSoonTab
              title="Creators Hub"
              description="Connect with top creators in your niche, collaborate on projects, and grow your network."
            />
          )}

          {/* ─── Tab 3: Coming Soon ────────────────────────────────────── */}
          {activeTab === 3 && (
            <ComingSoonTab
              title="Analytics"
              description="Deep insights into your content performance, audience engagement, and revenue trends."
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default Studio;
